<template>
  <div class="main-content-container full-bg">
    <b-row>
      <b-col>
        <div
          class="top-content-container edit-pa-profile position-relative mb-5"
        >
          <b-breadcrumb class="pb-2">
            <b-breadcrumb-item>
              <span
                @click="navigateToCustomers"
                class="text-uppercase font-family-medium"
              >
                Customers
              </span>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              <span class="text-uppercase font-family-medium"
                >Add Customer</span
              >
            </b-breadcrumb-item>
          </b-breadcrumb>
          <h1 class="font-lg font-family-medium primary-dark content-title">
            Add Customer
          </h1>
          <b-form
            class="col-md-6 mx-auto"
            @submit.prevent="createUserAndCompanyIfNeeded"
            @keypress.enter.prevent="createUserAndCompanyIfNeeded"
          >
            <b-form-group
              class="d-flex flex-column form-group-container"
              label="Add new customer"
            >
              <div v-if="showError" style="margin-top: 1rem">
                <li v-for="error in errors" :key="error.message">
                  <h6
                    style="margin-top: 0.2rem; margin-bottom: 1rem"
                    class="font-sm font-family-regular danger position-relative"
                    data-cy="customer-add-error-generic"
                  >
                    {{ getErrInSentenceCase(error) }}
                  </h6>
                </li>
              </div>

              <ValidationObserver ref="observer">
                <b-card class="form-group-card d-flex flex-column">
                  <div class="form-group-top d-flex mt-2">
                    <b-row class="mx-0 px-0 col-12 w-full">
                      <CompanyEditCreate
                        :companyNames="companyNames"
                        :memberships="memberships"
                        :currentMembershipOption="currentMembershipOption"
                        :headroomEntities="headroomEntityOptions"
                        :selectedMembershipId="getMembershipIdIfExists(company)"
                        :can-close-sub-modal="canCloseSubModal"
                        :success="success"
                        :membership-error="membershipError"
                        @selected-company-id="setCompanyFromId"
                        @new-company-name="setNewCompanyName"
                        @new-company-entity="setNewCompanyBillingEntity"
                        @confirm-update-membership="updateMembership"
                        @cancel-update-membership="revertMembership"
                      />
                    </b-row>
                  </div>

                  <div name="name row" class="form-group-top d-flex mt-3">
                    <b-row class="mx-0 px-0 col-12 d-flex justify-between">
                      <b-col class="px-0 pr-4 mx-0" md="6">
                        <ValidationProvider
                          name="First Name field"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <b-form-group
                            label="First name *"
                            for="First Name field"
                            class="position-relative pb-3"
                          >
                            <b-form-input
                              :disabled="success"
                              autocomplete="off"
                              size="lg"
                              type="text"
                              class="cy-add-cust-fname"
                              placeholder="First name"
                              v-model="firstName"
                              data-cy="customer-add-field-first-name"
                              v-on:keypress="isLetter($event)"
                            ></b-form-input>
                          </b-form-group>
                          <h6
                            v-if="errors[0]"
                            class="
                              font-sm
                              cy-add-cust-fname-err
                              font-family-regular
                              position-relative
                              danger
                              reduce-line-height
                              mt-2
                            "
                            data-cy="customer-add-field-error-first-name"
                          >
                            {{ errors[0] }}
                          </h6>
                        </ValidationProvider>
                      </b-col>

                      <b-col name="last-name-col" class="px-0 pl-4 mx-0" md="6">
                        <ValidationProvider
                          name="Last Name"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <b-form-group
                            label="Last name *"
                            for="Last Name field"
                            class="position-relative pb-3"
                          >
                            <b-form-input
                              autocomplete="off"
                              size="lg"
                              type="text"
                              placeholder="Last name"
                              class="cy-add-cust-lname"
                              :disabled="success"
                              v-model="lastName"
                              data-cy="customer-add-field-last-name"
                              v-on:keypress="isLetter($event)"
                            ></b-form-input>
                            <h6
                              v-if="errors[0]"
                              class="
                                font-sm
                                cy-add-cust-lname-err
                                font-family-regular
                                position-relative
                                danger
                                reduce-line-height
                                mt-2
                              "
                              data-cy="customer-add-field-error-last-name"
                            >
                              {{ errors[0] }}
                            </h6>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                    </b-row>
                  </div>

                  <div name="email row" class="form-group-top d-flex mt-2">
                    <b-row class="mx-0 px-0 col-12 w-full">
                      <b-col class="px-0" md="12">
                        <ValidationProvider
                          name="Email"
                          rules="email|required"
                          v-slot="{ errors }"
                        >
                          <b-form-group
                            label="Email *"
                            for="Email"
                            class="position-relative pb-3"
                          >
                            <b-form-input
                              autocomplete="off"
                              :disabled="success"
                              size="lg"
                              type="email"
                              placeholder="Email"
                              v-model="email"
                              data-cy="customer-add-field-email"
                            ></b-form-input>
                          </b-form-group>
                          <h6
                            v-if="errors[0]"
                            class="
                              font-sm
                              cy-add-cust-email-err
                              font-family-regular
                              position-relative
                              danger
                              reduce-line-height
                              mt-2
                            "
                            data-cy="customer-add-field-error-email"
                          >
                            {{ errors[0] }}
                          </h6>
                        </ValidationProvider>
                      </b-col>
                    </b-row>
                  </div>

                  <div
                    name="button-container"
                    class="
                      d-flex
                      align-items-center
                      justify-content-center
                      invite-btn-container
                    "
                  >
                    <b-button
                      @click="createUserAndCompanyIfNeeded"
                      :disabled="isProcessing || success"
                      class="btn-pill invite-btn cy-add-customer-btn mt-3"
                      data-cy="customer-add-button"
                    >
                      <b-spinner
                        style="width: 14px; height: 14px"
                        v-if="isProcessing"
                        variant="light"
                        label="Spinning"
                      ></b-spinner>
                      {{ `${!isProcessing ? "Add Customer" : ""}` }}
                    </b-button>
                  </div>
                </b-card>
              </ValidationObserver>
            </b-form-group>
          </b-form>
        </div>
      </b-col>
    </b-row>

    <SaveIndicator ref="saveIndicator" />
  </div>
</template>

<script>
import {
  ValidationProvider,
  ValidationObserver,
  extend
} from "vee-validate/dist/vee-validate.full";
import { email } from "vee-validate/dist/rules";
import { createCustomer } from "@/apis/auth";
import { mapState } from "vuex";
import {
  getCompaniesMinimal,
  getCompanyById,
  createCompany,
  updateCompany,
  getMemberships
} from "@/apis/companies";

import SaveIndicator from "@/components/indicators/SaveIndicator.vue";
import CompanyEditCreate from "@/views/admin/CompanyEditCreate.vue";
extend("email", {
  ...email,
  message: "Email is not valid"
});
extend("required", {
  message: () => "This is a required field"
});
let generalErrorMessage = "This field can only contain alphabets";
extend("name", {
  message: () => `${generalErrorMessage}`,
  validate: value => {
    let mustContainTheseChars = /^[a-zA-Z ]*$/;
    let containsRequiredChars = mustContainTheseChars.test(value);
    if (containsRequiredChars) {
      return true;
    } else {
      generalErrorMessage = "This field can only contain alphabets";
      return false;
    }
  }
});

export default {
  // ! File Name and defined name are completely different.
  // ! Could cause confusion with debugging.
  name: "ProfileSettings",

  components: {
    ValidationObserver,
    ValidationProvider,
    SaveIndicator,
    CompanyEditCreate
  },

  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      isProcessing: false,
      success: false,
      showError: false,
      errors: [],
      membershipError: "",
      customerId: "",

      company: {},
      selectedCompany: {},
      previousCompany: {},
      companyNames: [],
      memberships: [],
      // * @suraj: to be replaced with values from constants file
      headroomEntityOptions: [
        { value: null, text: "Please select a Billing Entity" },
        { value: "EU", text: "Public Support Assistance BV (EU)" },
        { value: "US", text: "Public Support Assistance Services LLC (US)" }
      ],
      canCloseSubModal: false
    };
  },

  created() {
    // * No need to ensure the async sequence here.
    this.setMemberships();
    this.setCompaniesNames();
  },

  computed: {
    ...mapState("authModule", ["role"]),

    shouldCreateCompany() {
      return this.company.name && !this.company.id;
    },

    currentMembershipOption() {
      const current = this.company?.companyMembership?.membershipDetails;
      if (!current?.id) {
        return null;
      }

      return this.transformMembershipLabel(current);
    }
  },

  methods: {
    // todo: can be a mixin [business level]
    async setCompaniesNames(getActiveOnes = true) {
      const {
        response: { data: { results: names } = {} } = {},
        error
      } = await this.asyncWrap(getCompaniesMinimal, getActiveOnes);

      if (error) {
        const message = `
        Failed to load the list of company names.
        Please refresh to try again. If the problem persists, kindly contact
        your admin.`;
        this.$refs.alert.open(message);
        return;
      }

      const list = names.map(({ id, name }) => ({ value: id, text: name }));
      const empty = { value: null, text: "Select a company" };

      this.companyNames = [empty, ...list];
    },

    // todo: can be a mixin [business level]
    async setMemberships() {
      const {
        response: { data: { results: memberships } = {} } = {},
        error
      } = await this.asyncWrap(getMemberships);

      if (error) {
        const message = `
        Failed to load the list of memberships.
        Please refresh to try again. If the problem persists, kindly contact
        your admin.`;
        this.$refs.alert.open(message);
        return;
      }

      const list = memberships.map(this.transformMembershipLabel);
      const sorted = this.sortByText(list);

      const empty = { value: null, text: "Please select a membership" };
      this.memberships = [empty, ...sorted];
    },

    // todo: make it a helper
    sortByText(list) {
      return list.sort((a, b) => {
        const textA = a.text.toUpperCase();
        const textB = b.text.toUpperCase();

        if (textA < textB) {
          return -1;
        }

        if (textA > textB) {
          return 1;
        }

        return 0;
      });
    },

    getMembershipIdIfExists(company) {
      return company?.companyMembership?.membershipDetails?.id;
    },

    async setCompanyFromId(companyId) {
      this.$refs.saveIndicator.showSaveIndicator();
      const {
        response: { data: fetchedCompany = {} } = {},
        error
      } = await this.asyncWrap(getCompanyById, companyId);

      this.$refs.saveIndicator.clearSaveIndicators();

      if (error) {
        this.handleErrors(error);
        return;
      }

      // * we need to keep track of changes to see if we need to update
      this.previousCompany = {
        ...(this.company.id ? this.company : fetchedCompany)
      };
      this.company = fetchedCompany || {};
    },

    setNewCompanyName(name) {
      this.company = {};
      this.company.name = name;
    },

    setNewCompanyBillingEntity(entity) {
      this.company.headroomEntity = entity;
    },

    getErrInSentenceCase(error) {
      return error.charAt(0).toUpperCase() + error.slice(1);
    },

    navigateToCustomers() {
      if (this.role === "assistant") {
        this.$router.push({ name: "DedicatedCustomers" });
      } else if (this.role === "admin") {
        this.$router.push({
          name: "CustomersListAdmin",
          query: {
            tab: 0
          }
        });
      }
    },

    assignPa() {
      this.$router.push({
        path: `/admin/new-customer-intake/${this.customerId}`
      });
    },

    async createUserAndCompanyIfNeeded() {
      const valid = await this.$refs.observer.validate();
      const isInvalid = !valid;
      if (isInvalid) {
        return;
      }

      if (this.shouldCreateCompany) {
        this.$refs.saveIndicator.showSaveIndicator();

        const {
          response: { data: createdCompany } = {},
          error
        } = await this.asyncWrap(createCompany, this.company);

        this.$refs.saveIndicator.clearSaveIndicators();

        if (error) {
          this.handleErrors(error);
          return;
        }

        this.company = createdCompany;
      }

      this.isProcessing = true;

      const data = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        company: this.company.id
      };
      // todo: use the async wrapper pattern
      createCustomer(data)
        .then(res => {
          this.customerId = res.data.profile;
          this.success = true;
          this.assignPa();
        })
        .catch(err => {
          this.showError = true;
          if (err.response.status === 401) {
            this.errors = [
              "User token expired. Please logout and login again."
            ];
          } else {
            const message =
              (err.response.data?.email && err.response.data?.email[0])
              || err.response.data?.detail;
            this.errors = [message];
          }
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },

    async updateMembership(companyWithNewMembership) {
      const id = companyWithNewMembership.data.id;
      await this.updateCompany(companyWithNewMembership, id);
    },

    revertMembership() {
      const prevMembership = this.previousCompany.companyMembership;
      this.company.companyMembership = { ...prevMembership };
      this.clearErrors();
    },

    async updateCompany({ data }, companyId) {
      this.$refs.saveIndicator.showSaveIndicator();

      this.canCloseSubModal = false;

      if (data) {
        // * Suggested pattern for handling async network calls
        const {
          response: { data: updatedCompany } = {},
          error
        } = await this.asyncWrap(updateCompany, companyId, data);

        this.$refs.saveIndicator.clearSaveIndicators();

        if (error) {
          this.handleErrors(error);
          return;
        }

        this.company = updatedCompany;
      }

      this.canCloseSubModal = true;
      this.clearErrors();
    },

    // todo: can be a helper [business level]
    transformMembershipLabel({
      id,
      title,
      totalHours,
      allowedFlexibleHours,
      maxSeats
    }) {
      const text = this.getMembershipCustomLabel({
        title,
        totalHours,
        allowedFlexibleHours,
        maxSeats
      });

      return {
        value: id,
        text: text,
        allowedFlexibleHours,
        maxSeats
      };
    },

    // todo: can be a helper [business level]
    getMembershipCustomLabel({
      title,
      totalHours,
      allowedFlexibleHours,
      maxSeats
    }) {
      let seatsPrefix = maxSeats ? maxSeats : "unlimited";
      let hoursPrefix = totalHours ? totalHours : "unlimited";
      const seatOrSeats = maxSeats > 1 || maxSeats === 0 ? "seats" : "seat";

      if (allowedFlexibleHours) {
        hoursPrefix = "flexible";
      }

      return `${title} (${hoursPrefix} hours & ${seatsPrefix} ${seatOrSeats})`;
    },

    clearErrors() {
      this.errors = [];
      this.membershipError = "";
    },

    // todo: can be a helper [utils level]
    handleErrors(error) {
      const nonFieldErrors = error?.response?.data?.nonFieldErrors;
      if (nonFieldErrors) {
        const isTotalCustomersExceeded =
          nonFieldErrors[0] ===
          "Company have more users than the membership allows.";
        isTotalCustomersExceeded
          ? (this.membershipError = nonFieldErrors[0])
          : (this.errors = nonFieldErrors);
        return;
      }

      if (error) {
        console.error(error);
        const message =
          "Failed to create a new company. Please try again. If the problem persists, please contact your admin.";
        this.errors = [message];
        return;
      }
    },

    // todo: can be a helper [utils level]
    async asyncWrap(fn, ...args) {
      let response;
      let error;

      this.isProcessing = true;

      try {
        response = await fn(...args);
      } catch (caughtError) {
        error = caughtError;
      }

      this.isProcessing = false;

      return { response, error };
    },

    isLetter(e) {
      let char = String.fromCharCode(e.keyCode);
      if (/^[A-Za-z\s]+$/.test(char)) return true;
      else e.preventDefault();
    }
  }
};
</script>

<style scoped lang="scss">
.form-group-container {
  margin: 7rem auto 0 auto;
}
.first-name-column {
  margin-right: 2.5rem;
}
.form-group-card {
  padding: 1rem 1.8rem 0 1.8rem;
}
.invite-btn-container {
  margin-top: 2.5rem;
  margin-bottom: 1.5rem;
}
.invite-btn {
  width: 20.8rem;
  padding: 0.6rem 0;
}
.assign-btn {
  width: 10.8rem;
  padding: 0.6rem 0;
}
</style>
